import { useState, Fragment, useRef, useEffect } from 'react';
import { Flex, Box, Text, Heading, Button } from 'rebass/styled-components';
import { ICustomAssignedPresetPlan, IPresetPlan, IPresetPlanSummary } from '../../../graphql/types/queries/presetPlans';
import { PresetPlanAnswers } from './PresetPlanAnswers';
import { HoverableCard } from '../LastCourses';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { IOkPayload } from '../../../graphql/types/mutations/generics';
import { toast } from 'react-toastify';
import { GET_AIP_COURSES_ENGAGEMENT_CHART_VALUES, GET_CUSTOM_ASSIGNED_PRESET_PLANS, GET_PRESET_PLAN_COURSES_ENGAGEMENT_CHART_VALUES } from '../../../graphql/queries';
import moment from 'moment';
import Modal from 'styled-react-modal';
import { SpecialModalBackground } from '../../../components/Commons';
import styled from 'styled-components';
import TextArea, { TextAreaNoForm } from '../../../components/TextArea';
import { BarChart, Bar, Legend, ResponsiveContainer, XAxis, YAxis, Tooltip, LineChart, Line } from "recharts"
import { ICourseEngagmentChartValue, IGetAIPEngagementChartValuesInput, IGetAIPEngagementChartValuesOutput, IGetPresetPlanEngagementChartValuesInput, IGetPresetPlanEngagementChartValuesOutput } from '../../../graphql/types/queries/course';

const Wrapper = styled(Flex)`
  overflow: auto;
  width: 600px;
  padding-right: 20px;
  margin-right: 20px;
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
`;
var enumerateDaysBetweenDates = function (startDate: moment.Moment, endDate: moment.Moment) {
  var now = startDate.clone(), dates = [];
  now.add(1, 'day')
  while (now.isBefore(endDate)) {
    dates.push(now.format('MM/DD/YYYY'));
    now.add(1, 'days');
  }
  return dates;
};

interface ICount {
  day: moment.Moment, count: number
}

export const CourseEngagementChart = ({ patientId, programId, close }: { patientId: string, programId: string, close: () => void }) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [chartData, setChartData] = useState<Array<ICourseEngagmentChartValue>>([])

  const [getFormContent, { data: dataFormContent, loading: loadingFormContent, error: errorFormContent }] = useLazyQuery<IGetAIPEngagementChartValuesOutput, IGetAIPEngagementChartValuesInput>(GET_AIP_COURSES_ENGAGEMENT_CHART_VALUES, {
    variables: {
      patientId
    },
    onCompleted: (e) => {
      if (!e || !e.getAIPEngagementChartValues) return
    }
  })
  const [getPresetPlan, { data: dataPresetPlan, loading: loadingPresetPlan, error: errorPresetPlan }] = useLazyQuery<IGetPresetPlanEngagementChartValuesOutput, IGetPresetPlanEngagementChartValuesInput>(GET_PRESET_PLAN_COURSES_ENGAGEMENT_CHART_VALUES, {
    variables: {
      patientId,
      programId
    },
    onCompleted: (e) => {
    }
  })
  useEffect(() => {
    if (!!loadingFormContent || !dataFormContent || !dataFormContent.getAIPEngagementChartValues) {
      setIsLoaded(true)
      return
    }

    const final: Array<ICourseEngagmentChartValue> = []
    const tmp: Array<ICount> = (JSON.parse(JSON.stringify(dataFormContent.getAIPEngagementChartValues)) as Array<ICourseEngagmentChartValue>).sort((a, b) => {
      const d1 = moment(a.day, 'MM/DD/YYYY')
      const d2 = moment(b.day, 'MM/DD/YYYY')

      if (d1.isBefore(d2)) return -1

      return 1
    }).map(x => {
      return {
        day: moment(x.day, 'MM/DD/YYYY'),
        count: x.count
      }
    })
    for (let index = 0; index < tmp.length; index++) {
      const element = tmp[index];
      final.push({ day: element.day.format('MM/DD/YYYY'), count: element.count })
      if (index < tmp.length - 1) {
        const dts = enumerateDaysBetweenDates(element.day, tmp[index + 1].day)
        for (let ii = 0; ii < dts.length; ii++) {
          const el = dts[ii];
          final.push({
            day: el,
            count: 0
          })
        }
      }
    }

    setChartData(final)
    setIsLoaded(true)
  }, [dataFormContent, loadingFormContent])

  useEffect(() => {
    if (!!loadingPresetPlan || !dataPresetPlan || !dataPresetPlan.getPresetPlanEngagementChartValues) {
      setIsLoaded(true)
      return
    }

    const final: Array<ICourseEngagmentChartValue> = []
    const tmp: Array<ICount> = (JSON.parse(JSON.stringify(dataPresetPlan.getPresetPlanEngagementChartValues)) as Array<ICourseEngagmentChartValue>).sort((a, b) => {
      const d1 = moment(a.day, 'MM/DD/YYYY')
      const d2 = moment(b.day, 'MM/DD/YYYY')

      if (d1.isBefore(d2)) return -1

      return 1
    }).map(x => {
      return {
        day: moment(x.day, 'MM/DD/YYYY'),
        count: x.count
      }
    })
    for (let index = 0; index < tmp.length; index++) {
      const element = tmp[index];
      final.push({ day: element.day.format('MM/DD/YYYY'), count: element.count })
      if (index < tmp.length - 1) {
        const dts = enumerateDaysBetweenDates(element.day, tmp[index + 1].day)
        for (let ii = 0; ii < dts.length; ii++) {
          const el = dts[ii];
          final.push({
            day: el,
            count: 0
          })
        }
      }
    }

    setChartData(final)
    setIsLoaded(true)
  }, [dataPresetPlan, loadingPresetPlan])

  useEffect(() => {
    if (!programId || programId === 'aipProgram') {
      getFormContent()
    } else {
      getPresetPlan()
    }
  }, [])
  if (!isLoaded) return null
  if (!chartData) {
    return (
      <Flex width={'100%'} height={'400px'} flexDirection={'column'}>
        <Heading mb={4} textAlign={'center'}>COURSE ENGAGEMENT</Heading>

        <Text textAlign={'center'}>No Engagement Found</Text>
        <Button alignSelf={'center'} mt={4} onClick={()=>{close()}}>Close</Button>
      </Flex>
    )
  }
  const CustomizedDot = (props:any) => {
    const { cx, cy, stroke, payload, value } = props;
  
    if (payload.count) {
      return (
        <svg x={cx - 4} y={cy - 4} width={8} height={8} fill="white">
          <g transform="translate(4 4)">
            <circle r="4" fill="black" />
            <circle r="2" fill="white" />
          </g>
        </svg>
      );
    }
  
    return null;
  };
  
  return (
    <Flex width={'100%'} height={'400px'} flexDirection={'column'}>
      <Heading mb={4} textAlign={'center'}>COURSE ENGAGEMENT</Heading>
      <Text textAlign={'center'} mb={3}>Course's screen interacted with per day</Text>
      <ResponsiveContainer width="100%" height="100%">
        {/* <BarChart
          width={500}
          height={300} data={chartData}>
          <XAxis dataKey="day" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="count" fill="#8884d8" />
        </BarChart> */}
        <LineChart
          width={500}
          height={300}
          data={chartData}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <XAxis dataKey="day" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="count" stroke="#8884d8" dot={<CustomizedDot />} />
        </LineChart>
      </ResponsiveContainer>
      <Button alignSelf={'center'} mt={4}>Close</Button>
    </Flex>
  )
};
